import type { CalendarMessages } from './i18n.js';

/** English defaults — preserve the values the package previously
 *  hardcoded so non-i18n consumers see no behaviour change. */
export const defaultCalendarMessages: CalendarMessages = {
  title: 'Calendar',
  controlsAriaLabel: 'Calendar controls',
  viewSwitcherAriaLabel: 'Calendar view',
  eventsCountOne: '{count} event',
  eventsCountOther: '{count} events',
  eventsCountZero: 'No events',
  newEvent: 'New event',

  viewMonth: 'Month',
  viewWeek: 'Week',
  viewDay: 'Day',
  viewAgenda: 'Agenda',

  icsExport: 'Export .ics',

  prev: 'Previous',
  next: 'Next',
  today: 'Today',

  filtersAriaLabel: 'Calendar filters',
  allKinds: 'All kinds',
  filtersTrigger: 'Filters',
  filtersTriggerActive: 'Filters · {count}',
  filtersPanelTitle: 'Filters',
  filterSectionGroups: 'Groups',
  filterSectionStatus: 'Status',
  resetFilters: 'Reset',
  allStatuses: 'All statuses',
  statusFilterAriaLabel: 'Calendar status filter',

  statusScheduled: 'Upcoming',
  statusLive: 'In progress',
  statusPast: 'Past',
  statusCancelled: 'Cancelled',

  cellOverflow: '+{count} more',
  railAriaLabel: 'Selected day detail',
  railEyebrow: 'Selected day',
  emptyDayTitle: 'No events',
  emptyDayDescription: 'This day is clear.',
  dayDetailAriaLabel: 'Selected day events',
  addEventOnDay: 'Add event on this day',
  nothingScheduled: 'Nothing scheduled',
  nothingScheduledHint: 'Try a different filter or kind.',
  addFirstOfDay: 'Add the first event for this day.',
  pickAnotherDay: 'Tap another day to see its events.',
  quietDay: 'Quiet day',
  dayDragHint: 'Drag to schedule something new.',
  dayNoScheduled: 'No scheduled events.',
  atAGlance: 'At a glance',
  dayEventsScheduledOne: '{count} event scheduled.',
  dayEventsScheduledOther: '{count} events scheduled.',
  weekSummaryAriaLabel: 'Week summary',
  daySummaryAriaLabel: 'Day summary',
  thisWeek: 'This week',
  comingUp: 'Coming up',
  upcomingListAriaLabel: 'Upcoming events this week',

  agendaAriaLabel: 'Agenda summary',
  agendaNextUp: 'Next up',
  agendaThisView: 'This view',
  agendaByKind: 'By kind',
  agendaJumpToWeek: 'Jump to week',
  agendaWeekOf: 'Week of',
  agendaUpcomingStat: 'upcoming',
  agendaPastStat: 'past',
  agendaWeeksStat: 'weeks',

  sheetAriaLabel: 'Event details',
  sheetDate: 'Date',
  sheetTime: 'Time',
  sheetGroup: 'Group',
  sheetAlsoIn: 'Also in',
  sheetLocation: 'Location',
  sheetNotes: 'Notes',
  sheetOpen: 'Open',
  sheetEdit: 'Edit',
  sheetDelete: 'Delete',

  relativeToday: 'Today',
  relativeTomorrow: 'Tomorrow',
  relativeYesterday: 'Yesterday',
  relativeInDays: 'In {count} days',
  relativeDaysAgo: '{count} days ago',

  composerNewTitle: 'New event',
  composerEditTitle: 'Edit event',
  composerNewSubtitle: 'Schedule an event on the calendar.',
  composerEditSubtitle: 'Update the details and save.',
  composerModeAriaLabel: 'Compose mode',
  composerModeSingle: 'Single',
  composerModeSingleSub: 'One event',
  composerModeBatch: 'Batch',
  composerModeBatchSub: 'Many at once',
  composerModeAi: 'From notes',
  composerModeAiSub: 'AI extract',
  composerSectionKind: 'Kind',
  composerSectionWhat: 'What',
  composerSectionWhen: 'When',
  composerSectionAudience: 'Audience',
  composerSectionLocation: 'Location & meeting',
  composerSectionRepeat: 'Repeat',
  composerSectionReminder: 'Reminder',
  composerSectionNote: 'Note',
  composerSectionPreview: 'Preview',
  composerSectionPaste: 'Paste events',
  composerSectionPasteAi: 'Paste an email, plan, or notes',
  composerSectionSuggested: 'Suggested events',
  composerEventKindLabel: 'Event kind',
  composerTitleLabel: 'Title',
  composerLinkedLabel: 'Linked content (optional)',
  composerDateLabel: 'Date',
  composerTimeLabel: 'Time',
  composerDurationLabel: 'Duration',
  composerPrimaryGroupLabel: 'Primary group',
  composerPrimaryGroupNone: 'Private (just me)',
  composerCrossPostLabel: 'Cross-post to (optional)',
  composerLocationTypeLabel: 'Location type',
  composerLocationNone: 'No location',
  composerLocationOnline: 'Online',
  composerLocationInPerson: 'In person',
  composerLocationCustom: 'Custom',
  composerMeetingProviderLabel: 'Meeting method',
  composerMeetingDetailLabel: 'Meeting number or link',
  composerMeetingDetailPlaceholder: 'e.g. 123 456 789 or https://...',
  composerLocationTextLabel: 'Location detail',
  composerLocationInPersonPlaceholder: 'e.g. Room 301',
  composerLocationCustomPlaceholder: 'e.g. To be confirmed',
  meetingProviderTencent: 'Tencent Meeting',
  meetingProviderZoom: 'Zoom',
  meetingProviderGoogleMeet: 'Google Meet',
  meetingProviderTeams: 'Microsoft Teams',
  meetingProviderOther: 'Other online',
  composerRepeatLabel: 'Repeat',
  composerRepeatFrequencyLabel: 'Frequency',
  composerRepeatUntilLabel: 'Repeat until',
  composerRepeatUntilPlaceholder: 'No end date',
  composerReminderLabel: 'Reminder',
  composerDefaultGroupLabel: 'Default group',
  composerDefaultReminderLabel: 'Default reminder',
  composerTitlePlaceholders: {
    default: 'Event title',
    publish: 'Issue #72 — Title',
    newsletter: 'Weekly note · Title',
    survey: 'Reader survey · Title',
    thread: 'Forum prompt · Title',
    workshop: 'Workshop session · Title',
    deadline: 'Deadline · Title',
    booking: 'Office hours · Host',
  },
  composerDatePlaceholder: 'Pick a date',
  composerDatePickerClear: 'Clear',
  composerDatePickerDialogLabel: 'Choose a date',
  composerLinkedNone: 'None',
  composerNotePlaceholder: 'e.g. Bring drafts · Issue #71 · 12 seats open',
  composerBatchPlaceholder:
    '# Paste one event per line. Format is forgiving — date, time, kind, title.\n\n2026-05-12 09:00 · publish · Issue #71 — On Quiet Interfaces\n2026-05-13 14:00 workshop Quiet Interfaces · workshop session 4\n2026-05-14, 11:00 — survey: Reader letters, May\n2026-05-15 18:00 thread Friday crit\nMay 18 09:00 newsletter — Weekly note\n2026-05-20 16:30 booking · Office hours · Mira',
  composerAiPlaceholder:
    "Hi —\n\nPutting together May. Here's what I have so far:\n\n- Issue #71 (Quiet Interfaces essay) goes out Tue May 12 at 9am.\n- Friday crit is moving to 4pm starting May 15. Repeat weekly.\n- Workshop session 4 (Quiet Interfaces cohort): May 13, 2pm.\n- Reader survey opens May 14 — let's run it for a week.\n- Office hours next Wednesday at 4:30pm with the inner circle.\n- Newsletter goes out every Sunday at 9am through end of month.",
  composerDurationAllDay: 'All day',
  composerDurationMinutes: '{count} minutes',
  composerDurationOneHour: '1 hour',
  composerDurationOneHalfHour: '1h 30m',
  composerDurationTwoHours: '2 hours',
  composerRepeatDaily: 'Every day',
  composerRepeatWeekly: 'Every week',
  composerRepeatBiweekly: 'Every 2 weeks',
  composerRepeatMonthly: 'Every month',
  composerReminderNone: 'No reminder',
  composerReminderByKind: 'By event kind',
  composerReminderMinutes: '{count} minutes before',
  composerReminderOneHour: '1 hour before',
  composerReminderOneDay: '1 day before',
  composerSave: 'Save',
  composerAdd: 'Add to calendar',
  composerCancel: 'Cancel',
  composerDelete: 'Delete',
  composerAddEventsZero: 'Add events',
  composerAddEventsOne: 'Add {count} event',
  composerAddEventsOther: 'Add {count} events',
  composerSkip: 'Skip',
  composerRestore: 'Restore',
  composerExtract: 'Extract',
  composerReading: 'Reading…',
  composerReExtract: 'Re-extract',
  composerBatchClear: 'Clear',
  composerBatchParsedTag: '{count} parsed',
  composerBatchUnclearTag: '{count} unclear',
  composerBatchEmptyTitle: 'Nothing parsed',
  composerBatchEmptyDesc: 'Paste lines above to see them parsed here.',
  composerAiLoadingTitle: 'Reading…',
  composerAiLoadingDesc: 'The model is pulling events out of your text.',
  composerAiPromptTitle: 'Ready to extract',
  composerAiPromptDesc: 'Paste an email or notes above, then tap Extract.',
  composerAiEmptyTitle: 'No events detected',
  composerAiEmptyDesc: 'Try giving more detail (date, time, who, what).',
  composerAiRepeatsTag: 'repeats',
  composerAiPoweredTag: 'AI',
  composerAiFallbackNote:
    'AI is unavailable ({reason}); showing local heuristic suggestions instead.',
  composerErrorTitleRequired: 'Title is required.',
  composerErrorDateRequired: 'Pick a date.',
  composerErrorRepeatUntilBeforeStart: 'Repeat-until date must be on or after the start date.',
  composerErrorDuplicateEvent: 'This event already exists on the calendar.',
  composerCrossPostEmpty: 'No other groups available',
  composerActionsAriaLabel: 'Composer actions',
  composerParsedListAriaLabel: 'Parsed events',
  composerSuggestedListAriaLabel: 'Suggested events',
  composerRelatedEventsTitle: 'Related events',
  composerRelatedEventsExactTitle: 'Already on the calendar',
  composerRelatedEventsHint: 'Open an existing event instead of creating it twice.',

  eventCrossPostedTo: 'Cross-posted to: {groups}',
  eventRepeatsLabel: 'Repeats',
  monthCellAriaLabel: '{date}, {countLabel}',
  dayEventsBadgeLabel: '{countLabel} on this day',
  eventActionsAriaLabel: 'Event actions',
  eventMenuOpen: 'Open',
  eventMenuEdit: 'Edit...',
  eventMenuDuplicate: 'Duplicate',
  eventMenuMoveToToday: 'Move to today',
  eventMenuUncancel: 'Uncancel',
  eventMenuCancel: 'Cancel event',
  eventMenuDelete: 'Delete',

  schedulingBookSlotTitle: 'Book a slot',
  schedulingConfirmBooking: 'Book this slot',
  schedulingNoSlotSelected: 'No slot selected.',
  schedulingBookingDoneTitle: "You're on the calendar",
  schedulingBookingDoneBody: 'Confirmation email + calendar invite sent. See you {time} on {day}.',
  schedulingDate: 'Date',
  schedulingTime: 'Time',
  schedulingWhere: 'Where',
  schedulingLength: 'Length',
  schedulingLengthMinutes: '{count} min',
  schedulingTopicLabel: 'What do you want to discuss?',
  schedulingTopicPlaceholder: 'A draft, a question, or just want to say hi...',
  schedulingSlotOpen: 'Open',
  schedulingAvailabilityOn: 'Availability on {date}',
  schedulingWithPeople: 'With people',
  schedulingSlotsOpen: '{open} of {total} slots open',
  schedulingSeats: 'seats',
  schedulingCapacityText: '{filled}/{total} {label}',
  schedulingFullWaitlist: 'Full · waitlist',
  schedulingCapacityTaken: '{filled} of {total} {label} taken',
  schedulingWithHost: 'with {host}',
  schedulingJoining: 'Joining:',
  schedulingAttendees: '{count} attendees',
  schedulingJoinWaitlist: 'Join waitlist',
  schedulingSaveMySeat: 'Save my seat',
  schedulingRecurringFallback: 'recurring',
  schedulingRecurringWithHost: '{rule}, {start} - {end} with {host}',
  schedulingSubscribed: '{count} subscribed',
  schedulingSeatsPerSession: '{count} seats per session',
  schedulingFollow: 'Follow',
  schedulingBack: 'Back',
  schedulingOfficeHours: 'Office hours',
  schedulingSessionLength: '{count} min sessions',
  schedulingAvailableSlots: 'Available slots',
  schedulingOpenSlots: '{count} open',
  schedulingFullyBooked: 'Fully booked',
  schedulingGroupSessions: 'Group sessions',
  schedulingGroupSessionsSubtitle: 'Capacity-limited. Save a seat or join the waitlist.',
  schedulingSeries: 'Series',
  schedulingSeriesSubtitle: 'Subscribe once, attend many.',
  schedulingSessions: '{count} sessions',
  schedulingSubscribeToSeries: 'Subscribe to series',
  schedulingRecurringAvailability: 'Recurring availability',
  schedulingRecurringAvailabilitySubtitle: 'Follow once — every future session shows up on your calendar.',
};
